import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDT, peso } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [contracts, setContracts] = useState([]);
  //: The itemised build-up, per contract. Fetched rather than stored: it has to
  //: move when a wage order, the payroll factor or a statutory table moves, and
  //: a saved copy would quote yesterday's law.
  const [cost, setCost] = useState({});
  const [costNight, setCostNight] = useState({});
  const [posts, setPosts] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", contact_person: "", contact_email: "", contact_phone: "", address: "" });
  const [cForm, setCForm] = useState({ start_date: "", end_date: "", coverage: "", pay_rate_daily: "", bill_rate_daily: "", vat_percent: 12, invoice_frequency: "monthly", fee_label: "", fee_amount: "" });
  const [cOpen, setCOpen] = useState(false);
  const [amend, setAmend] = useState(null);
  const [amendForm, setAmendForm] = useState({ end_date: "", pay_rate_daily: "", bill_rate_daily: "", coverage: "", reason: "" });
  const [rateFor, setRateFor] = useState(null);
  const [rateForm, setRateForm] = useState({ post_id: "", shift_type: "", day_type: "", pay_rate_daily: "", bill_rate_daily: "", effective_from: "" });

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/posts").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  const pick = async (c) => {
    setSelected(c);
    const { data } = await api.get(`/contracts?client_id=${c.id}`);
    setContracts(data);
    setCost({});
    data.forEach((ct) =>
      api.get(`/contracts/${ct.id}/cost-distribution`)
        .then((r) => setCost((p) => ({ ...p, [ct.id]: r.data })))
        .catch(() => {}));
  };

  const createClient = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setNewOpen(false);
      setForm({ name: "", code: "", contact_person: "", contact_email: "", contact_phone: "", address: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createContract = async () => {
    try {
      const payload = {
        client_id: selected.id, start_date: cForm.start_date, end_date: cForm.end_date, coverage: cForm.coverage,
        pay_rate_daily: parseFloat(cForm.pay_rate_daily) || 0, bill_rate_daily: parseFloat(cForm.bill_rate_daily) || 0,
        vat_percent: parseFloat(cForm.vat_percent) || 0, invoice_frequency: cForm.invoice_frequency,
        fixed_fees: cForm.fee_label ? [{ label: cForm.fee_label, amount: parseFloat(cForm.fee_amount) || 0 }] : [],
      };
      await api.post("/contracts", payload);
      toast.success("Contract created");
      setCOpen(false);
      pick(selected);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitAmend = async () => {
    const changes = {};
    if (amendForm.end_date) changes.end_date = amendForm.end_date;
    if (amendForm.pay_rate_daily) changes.pay_rate_daily = parseFloat(amendForm.pay_rate_daily);
    if (amendForm.bill_rate_daily) changes.bill_rate_daily = parseFloat(amendForm.bill_rate_daily);
    if (amendForm.coverage) changes.coverage = amendForm.coverage;
    try {
      await api.post(`/contracts/${amend.id}/amend`, { reason: amendForm.reason, changes });
      toast.success("Amendment recorded (version preserved)");
      setAmend(null);
      pick(selected);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitRate = async () => {
    try {
      await api.post(`/contracts/${rateFor.id}/rates`, {
        post_id: rateForm.post_id || null, shift_type: rateForm.shift_type || null, day_type: rateForm.day_type || null,
        pay_rate_daily: parseFloat(rateForm.pay_rate_daily) || 0, bill_rate_daily: parseFloat(rateForm.bill_rate_daily) || 0,
        effective_from: rateForm.effective_from,
      });
      toast.success("Rate rule added");
      setRateFor(null);
      pick(selected);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients & Contracts" sub="Client companies, contracts, rate rules and amendments"
        actions={<Button data-testid="new-client-button" className="rounded-none" onClick={() => setNewOpen(true)}>New client</Button>} />
      <DataTable testid="clients-table" onRowClick={pick} rows={clients}
        columns={[
          { key: "ref_no", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.ref_no}</span> },
          { key: "name", label: "Client", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.code}</div></div> },
          { key: "contact_person", label: "Contact", render: (r) => <div><div>{r.contact_person}</div><div className="text-xs text-muted-foreground">{r.contact_phone}</div></div> },
          { key: "address", label: "Address" },
        ]} />

      {selected && (
        <div className="mt-8" data-testid="client-contracts">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold">Contracts — {selected.name}</h2>
            <Button data-testid="new-contract-button" variant="outline" className="rounded-none" onClick={() => setCOpen(true)}>New contract</Button>
          </div>
          {contracts.length === 0 && <div className="font-mono text-xs text-muted-foreground border border-border p-6">No contracts yet</div>}
          <div className="space-y-4">
            {contracts.map((c) => (
              <div key={c.id} className="border border-border bg-card p-4" data-testid={`contract-${c.ref_no}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs">{c.ref_no}</span>
                    <StatusBadge value={c.status} />
                    <span className="text-sm font-semibold">{c.start_date} → {c.end_date}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-none" data-testid={`amend-${c.ref_no}`} onClick={() => { setAmend(c); setAmendForm({ end_date: "", pay_rate_daily: "", bill_rate_daily: "", coverage: "", reason: "" }); }}>Amend</Button>
                    <Button size="sm" variant="outline" className="rounded-none" data-testid={`addrate-${c.ref_no}`} onClick={() => { setRateFor(c); setRateForm({ post_id: "", shift_type: "", day_type: "", pay_rate_daily: "", bill_rate_daily: "", effective_from: "" }); }}>Add rate rule</Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Pay rate / day</div><div className="font-mono">{peso(c.pay_rate_daily)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Bill rate / day</div><div className="font-mono">{peso(c.bill_rate_daily)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">VAT</div><div className="font-mono">{c.vat_percent}%</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Invoicing</div><div className="font-mono">{c.invoice_frequency}</div></div>
                </div>
                {c.coverage && <div className="text-sm text-muted-foreground mt-2">{c.coverage}</div>}
                {/* ---- the DOLE cost distribution -------------------------
                    A PH service agreement is not a daily rate, it is an
                    itemised monthly build-up per guard. Without it an agency
                    cannot bid a government contract, and nobody can tell
                    whether the bill rate covers the wage the law requires. */}
                {cost[c.id] && <CostPanel cd={cost[c.id]} nightCd={costNight[c.id]}
                                          onNight={() => {
                                            if (costNight[c.id]) { setCostNight((p) => ({ ...p, [c.id]: null })); return; }
                                            api.get(`/contracts/${c.id}/cost-distribution?night=true`)
                                              .then((r) => setCostNight((p) => ({ ...p, [c.id]: r.data })))
                                              .catch(() => {});
                                          }} />}

                {(c.rates || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Rate rules</div>
                    {c.rates.map((r) => (
                      <div key={r.id} className="font-mono text-xs border-t border-border py-1 flex justify-between">
                        <span>{posts.find((p) => p.id === r.post_id)?.name || "any post"} · {r.shift_type || "any shift"} · {r.day_type || "any day"} · from {r.effective_from}</span>
                        <span>pay {peso(r.pay_rate_daily)} / bill {peso(r.bill_rate_daily)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(c.amendments || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Amendment history</div>
                    {c.amendments.map((a) => (
                      <div key={a.version} className="text-xs border-t border-border py-1">
                        <span className="font-mono">v{a.version}</span> · {fmtDT(a.at)} · {a.by} — {a.reason}
                        <span className="text-muted-foreground"> ({Object.keys(a.changes).join(", ")})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none" data-testid="new-client-dialog">
          <DialogHeader><DialogTitle className="font-heading">New client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Company name"], ["code", "Code"], ["contact_person", "Contact person"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"], ["address", "Address"]].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider font-bold">{label}</Label>
                <Input data-testid={`client-${k}`} className="rounded-none mt-1" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
            <Button data-testid="client-create-submit" className="w-full rounded-none" onClick={createClient}>Create client</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-auto" data-testid="new-contract-dialog">
          <DialogHeader><DialogTitle className="font-heading">New contract — {selected?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Start</Label><Input data-testid="contract-start" type="date" className="rounded-none mt-1" value={cForm.start_date} onChange={(e) => setCForm({ ...cForm, start_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">End</Label><Input data-testid="contract-end" type="date" className="rounded-none mt-1" value={cForm.end_date} onChange={(e) => setCForm({ ...cForm, end_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Pay rate / day (₱)</Label><Input data-testid="contract-pay" type="number" className="rounded-none mt-1" value={cForm.pay_rate_daily} onChange={(e) => setCForm({ ...cForm, pay_rate_daily: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Bill rate / day (₱)</Label><Input data-testid="contract-bill" type="number" className="rounded-none mt-1" value={cForm.bill_rate_daily} onChange={(e) => setCForm({ ...cForm, bill_rate_daily: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">VAT %</Label><Input data-testid="contract-vat" type="number" className="rounded-none mt-1" value={cForm.vat_percent} onChange={(e) => setCForm({ ...cForm, vat_percent: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Invoice frequency</Label>
              <Select value={cForm.invoice_frequency} onValueChange={(v) => setCForm({ ...cForm, invoice_frequency: v })}>
                <SelectTrigger data-testid="contract-frequency" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="semi-monthly">Semi-monthly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Coverage</Label><Textarea data-testid="contract-coverage" className="rounded-none mt-1" value={cForm.coverage} onChange={(e) => setCForm({ ...cForm, coverage: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Fixed fee label (optional)</Label><Input data-testid="contract-fee-label" className="rounded-none mt-1" value={cForm.fee_label} onChange={(e) => setCForm({ ...cForm, fee_label: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Fixed fee amount</Label><Input data-testid="contract-fee-amount" type="number" className="rounded-none mt-1" value={cForm.fee_amount} onChange={(e) => setCForm({ ...cForm, fee_amount: e.target.value })} /></div>
          </div>
          <Button data-testid="contract-create-submit" className="w-full rounded-none mt-3" onClick={createContract}>Create contract</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!amend} onOpenChange={() => setAmend(null)}>
        <DialogContent className="rounded-none" data-testid="amend-dialog">
          <DialogHeader><DialogTitle className="font-heading">Amend {amend?.ref_no} — creates v{(amend?.amendments?.length || 0) + 1}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">New end date (optional)</Label><Input data-testid="amend-end" type="date" className="rounded-none mt-1" value={amendForm.end_date} onChange={(e) => setAmendForm({ ...amendForm, end_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">New pay rate / day</Label><Input data-testid="amend-pay" type="number" className="rounded-none mt-1" value={amendForm.pay_rate_daily} onChange={(e) => setAmendForm({ ...amendForm, pay_rate_daily: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">New bill rate / day</Label><Input data-testid="amend-bill" type="number" className="rounded-none mt-1" value={amendForm.bill_rate_daily} onChange={(e) => setAmendForm({ ...amendForm, bill_rate_daily: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">New coverage (optional)</Label><Input data-testid="amend-coverage" className="rounded-none mt-1" value={amendForm.coverage} onChange={(e) => setAmendForm({ ...amendForm, coverage: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Reason (required, audited)</Label><Textarea data-testid="amend-reason" className="rounded-none mt-1" value={amendForm.reason} onChange={(e) => setAmendForm({ ...amendForm, reason: e.target.value })} /></div>
            <Button data-testid="amend-submit" className="w-full rounded-none" onClick={submitAmend}>Record amendment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateFor} onOpenChange={() => setRateFor(null)}>
        <DialogContent className="rounded-none" data-testid="rate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rate rule — {rateFor?.ref_no}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Post (blank = any)</Label>
              <Select value={rateForm.post_id} onValueChange={(v) => setRateForm({ ...rateForm, post_id: v === "any" ? "" : v })}>
                <SelectTrigger data-testid="rate-post" className="rounded-none mt-1"><SelectValue placeholder="Any post" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any post</SelectItem>{posts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Shift type</Label>
              <Select value={rateForm.shift_type} onValueChange={(v) => setRateForm({ ...rateForm, shift_type: v === "any" ? "" : v })}>
                <SelectTrigger data-testid="rate-shift" className="rounded-none mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="day">Day</SelectItem><SelectItem value="night">Night</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Day type</Label>
              <Select value={rateForm.day_type} onValueChange={(v) => setRateForm({ ...rateForm, day_type: v === "any" ? "" : v })}>
                <SelectTrigger data-testid="rate-day" className="rounded-none mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="regular">Regular</SelectItem><SelectItem value="rest">Rest day</SelectItem><SelectItem value="holiday">Holiday</SelectItem><SelectItem value="special">Special</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Effective from</Label><Input data-testid="rate-effective" type="date" className="rounded-none mt-1" value={rateForm.effective_from} onChange={(e) => setRateForm({ ...rateForm, effective_from: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Pay rate / day</Label><Input data-testid="rate-pay" type="number" className="rounded-none mt-1" value={rateForm.pay_rate_daily} onChange={(e) => setRateForm({ ...rateForm, pay_rate_daily: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Bill rate / day</Label><Input data-testid="rate-bill" type="number" className="rounded-none mt-1" value={rateForm.bill_rate_daily} onChange={(e) => setRateForm({ ...rateForm, bill_rate_daily: e.target.value })} /></div>
          </div>
          <Button data-testid="rate-submit" className="w-full rounded-none mt-3" onClick={submitRate}>Add rule</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * The DOLE standard computation, per guard per month.
 *
 * ⚠️ Every figure is DERIVED — from the region's wage order, the contract's own
 * rates and fee, and the same statutory tables the payroll engine uses. A
 * build-up that quoted an SSS employer share payroll does not charge would
 * disagree with the agency's own books the first time anybody checked.
 */
function CostPanel({ cd, nightCd, onNight }) {
  const show = nightCd || cd;
  const money = (n) => peso(n);
  return (
    <div className="mt-4 border border-border p-3" data-testid={`cost-${cd.contract_ref}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
          Cost distribution — per guard, per month
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">
            {show.region || "no region"} · {show.wage_order?.order_no || "no wage order"} · factor {show.factor}
          </span>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]"
                  data-testid={`cost-night-${cd.contract_ref}`} onClick={onNight}>
            {nightCd ? "Day post" : "Night post"}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-6">
        <div>
          <Head>What the guard is paid</Head>
          {show.earnings.map((r, i) => <Line key={i} label={r.label} basis={r.basis} amount={money(r.amount)} />)}
          <Line label="Sub-total" amount={money(show.earnings_total)} strong />
          <Head>What the agency pays on top</Head>
          {show.employer.map((r, i) => <Line key={i} label={r.label} basis={r.basis} amount={money(r.amount)} />)}
          <Line label="Sub-total" amount={money(show.employer_total)} strong />
        </div>
        <div>
          <Head>What the client is charged</Head>
          <Line label="Total labour cost" amount={money(show.labour_cost)} strong />
          <Line label={`Administrative fee (${show.agency_fee_percent}% of labour)`}
                basis={`${(show.agency_fee_share_of_contract * 100).toFixed(1)}% of the contract`}
                amount={money(show.agency_fee)} />
          <Line label="Contract cost before VAT" amount={money(show.subtotal)} strong />
          <Line label={`VAT (${show.vat_percent}%)`} amount={money(show.vat)} />
          <div className="flex items-baseline justify-between gap-3 border-t-2 border-foreground pt-1.5 mt-1.5">
            <span className="font-heading font-bold text-sm">MONTHLY RATE PER GUARD</span>
            <span className="font-heading font-black text-lg tabular-nums">{money(show.total_monthly_per_guard)}</span>
          </div>
          {show.billed_monthly_per_guard ? (
            <div className="mt-1 text-[11px] flex items-baseline justify-between">
              <span className="text-muted-foreground">
                Actually billed at {peso(show.billed_monthly_per_guard / (show.factor / 12))}/day
              </span>
              <span className={`font-mono font-bold ${show.margin_monthly_per_guard >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--alert))]"}`}>
                {show.margin_monthly_per_guard >= 0 ? "+" : ""}{money(show.margin_monthly_per_guard)}
              </span>
            </div>
          ) : null}

          <div className="mt-3 space-y-1">
            {show.checks.map((ch) => (
              <div key={ch.id} className="flex gap-2 text-[11px] leading-snug" data-testid={`check-${ch.id}`}>
                <span className={`font-mono font-bold shrink-0 ${ch.ok === true ? "text-[hsl(var(--success))]" : ch.ok === false ? "text-[hsl(var(--alert))]" : "text-muted-foreground"}`}>
                  {ch.ok === true ? "OK" : ch.ok === false ? "NO" : "??"}
                </span>
                <span>
                  <span className={ch.ok === false ? "font-semibold" : ""}>{ch.label}</span>
                  <span className="block text-muted-foreground">{ch.detail}</span>
                </span>
              </div>
            ))}
          </div>
          {show.sites_without_region?.length > 0 && (
            <div className="mt-2 text-[10px] text-[hsl(var(--warning))] leading-snug">
              No wage region on {show.sites_without_region.join(", ")} — the minimum wage follows the post,
              so the floor cannot be checked for those.
            </div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border leading-relaxed">
        The 20% floor under RA 11917 is read here as the fee&apos;s share of the <span className="text-foreground">pre-VAT</span> contract
        cost, which is a 25% mark-up on labour. The other reading — 20% <em>on labour</em>, i.e. 16.7% of the
        contract — is the one that gets agencies cited. Which applies to a given agreement is a question for
        your counsel, not for this screen.
      </div>
    </div>
  );
}

function Head({ children }) {
  return <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-2 mb-1">{children}</div>;
}

function Line({ label, basis, amount, strong }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 text-[11px] ${strong ? "border-t border-border pt-1 mt-1 font-bold" : ""}`}>
      <span className={strong ? "" : "text-muted-foreground"}>
        {label}
        {basis && <span className="block font-mono text-[9px] opacity-70">{basis}</span>}
      </span>
      <span className="font-mono tabular-nums shrink-0">{amount}</span>
    </div>
  );
}
